import { Component, OnInit } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';
import { take } from 'rxjs/operators';

import { Table } from '../../../shared/table/table';
import { Popup } from '../../../../core/shared/components/popup/popup';
import { DynamicFieldConfig } from '../../../../core/shared/dynamic-form/form-config.model';
import { TableColumn, TableAction } from '../../../shared/models/table-models';
import { PickListService } from '../../../../core/shared/services/picklist.service';
import { PKL_GRP, DropdownOption } from '../../../../core/shared/models/picklist.model';
import { AppIcons } from '../../../../core/icons/fa-icons';
import { Setup } from '../../shared/service/setup';

@Component({
  selector: 'app-department-head',
  imports: [TranslatePipe, CommonModule, Table, Popup],
  templateUrl: './department-head.html',
  styleUrl: './department-head.css',
})
export class DepartmentHead implements OnInit {
  icons = AppIcons;

  list: any[] = [];
  columns: TableColumn[] = [];

  showPopup = false;
  isEditMode = false;
  formData: any = {};

  fields: DynamicFieldConfig[] = [];

  // ================= EMPLOYEE ASSIGNMENT =================
  showAssignPopup = false;
  assignFields: DynamicFieldConfig[] = [];
  assignFormData: any = {};
  private selectedDeptHeadSeq: number | null = null;

  constructor(
    private translate: TranslateService,
    private toastr: ToastrService,
    private router: Router,
    private setup: Setup,
    private picklist: PickListService,
  ) {}

  // ================= INIT =================
  ngOnInit(): void {
    this.loadPicklists();
  }

  // ================= PICKLIST =================
  loadPicklists() {
    this.picklist.loadCodes([PKL_GRP.COMMON_STATUS]);

    this.picklist.picklists$.pipe(take(1)).subscribe(() => {
      this.buildFields();
      this.buildColumns();
      this.buildAssignFields();
      this.loadData();
    });
  }

  // ================= FORM =================
  buildFields() {
    this.fields = [
      { key: 'deptSeq', label: 'SETUP.ID', type: 'text', hidden: true },
      {
        key: 'deptNm',
        label: 'SETUP.ORG.DEPARTMENT_NAME',
        type: 'text',
        validations: { required: true },
      },
      {
        key: 'status',
        label: 'COMMON.STATUS',
        type: 'select',
        options$: this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS),
        validations: { required: true },
      },
      { key: 'description', label: 'COMMON.DESCRIPTION', type: 'textarea' },
    ];
  }

  // ================= TABLE =================
  buildColumns() {
    this.columns = [
      { key: 'deptSeq', label: 'SETUP.ID' },
      {
        key: 'deptNm',
        label: 'SETUP.ORG.DEPARTMENT_NAME',
        type: 'link',
        onClick: (row: any) => this.goToTeamLead(row),
      },
      { key: 'description', label: 'COMMON.DESCRIPTION' },
      {
        key: 'status',
        label: 'COMMON.STATUS',
        type: 'badge',
        map: { 706: 'COMMON.ACTIVE', 707: 'COMMON.INACTIVE' },
        badgeMap: { 706: 'status-active', 707: 'status-inactive' },
      },
    ];
  }

  getDefaultStatus(): number {
    const options = this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS);
    return options?.[0]?.value || 706;
  }

  goToTeamLead(row: any) {
    this.router.navigate(['/team-lead', row.deptSeq]);
  }

  // ================= ACTIONS =================
  actions: TableAction[] = [
    {
      icon: this.icons.edit,
      tooltip: 'COMMON.EDIT',
      class: 'action-edit',
      onClick: (row) => this.openPopup(row),
    },
    {
      icon: this.icons.assignEmployee,
      tooltip: 'SETUP.EMPLOYEE_ASSIGNMENT',
      class: 'action-view',
      onClick: (row) => this.openAssignPopup(row),
    },
  ];

  // ================= LOAD =================
  loadData() {
    this.setup.getAllDepartments().subscribe({
      next: (res: any) => {
        const data = Array.isArray(res) ? res : res?.data || [];

        this.list = data.map((item: any) => ({
          deptSeq: item.deptSeq,
          deptNm: item.deptNm,
          description: item.dscr,
          status: item.sts ? 706 : 707,
        }));
      },
      error: () => {
        this.list = [];
      },
    });
  }

  // ================= POPUP =================
  openPopup(data?: any) {
    this.showPopup = true;

    if (data) {
      this.isEditMode = true;

      this.formData = {
        deptSeq: data.deptSeq,
        deptNm: data.deptNm,
        description: data.description,
        status: data.status,
      };
    } else {
      this.isEditMode = false;

      this.formData = {
        deptSeq: null,
        deptNm: '',
        description: '',
        status: this.getDefaultStatus(),
      };
    }
  }

  // ================= SAVE =================
  handleSave = (form: any) => {
    const payload = {
      deptSeq: this.isEditMode ? form.deptSeq : null,
      deptNm: form.deptNm,
      dscr: form.description ?? '',
      sts: form.status === 706,
      delFlg: 0,
    };

    this.setup.saveDepartment(payload).subscribe({
      next: () => {
        this.toastr.success(this.translate.instant(this.isEditMode ? 'SETUP.ORG.DEPARTMENT_HEAD_UPDATED' : 'SETUP.ORG.DEPARTMENT_HEAD_CREATED'));
        this.showPopup = false;
        this.loadData();
      },
      error: () => this.toastr.error(this.translate.instant('SETUP.SAVE_FAILED')),
    });
  };

  // ================= EMPLOYEE ASSIGNMENT =================
  private assignEmployees: any[] = [];
  private selectedDeptEmpSeq: number | null = null;
  private selectedRoleSeq: number | null = null;

  // Hardcoded userRoleId per department — resolved to a roleSeq and patched into the (always disabled) Roles field.
  private readonly DEPT_ROLE_ID_MAP: { [deptSeq: number]: string } = {
    1: 'PMDH',
    2: 'RMDH',
  };

  buildAssignFields(employeeOptions: DropdownOption[] = [], roleOptions: DropdownOption[] = []) {
    this.assignFields = [
      {
        key: 'regionManager',
        label: 'SETUP.ORG.DEPARTMENT_HEAD',
        type: 'select',
        options$: employeeOptions,
        validations: { required: true },
      },
      {
        key: 'coveringEmployee',
        label: 'SETUP.COVERING_EMPLOYEE',
        type: 'select',
        options$: employeeOptions,
        clearable: true,
      },
      {
        key: 'role',
        label: 'SETUP.ORG.ROLES',
        type: 'select',
        options$: roleOptions,
        validations: { required: true },
        disabled: true,
      },
      {
        key: 'fromDate',
        label: 'SETUP.COVERING_FROM_DATE',
        type: 'date',
      },
      {
        key: 'toDate',
        label: 'SETUP.COVERING_TO_DATE',
        type: 'date',
      },
    ];
  }

  openAssignPopup(row: any) {
    this.selectedDeptHeadSeq = row.deptSeq;
    this.selectedDeptEmpSeq = null;
    this.selectedRoleSeq = null;
    this.assignFormData = {};

    forkJoin({
      employees: this.setup.getAllEmployees(),
      roles: this.setup.getRoles(),
      deptEmps: this.setup.getDeptEmployees(row.deptSeq),
    }).subscribe({
      next: ({ employees, roles, deptEmps }) => {
        const employeeData = Array.isArray(employees) ? employees : (employees as any)?.data || [];
        this.assignEmployees = employeeData;

        const employeeOptions: DropdownOption[] = employeeData.map((item: any) => ({
          label: item.empNm,
          value: item.empSeq,
        }));

        const roleData = Array.isArray(roles) ? roles : (roles as any)?.data || [];
        const roleOptions: DropdownOption[] = roleData.map((item: any) => ({
          label: item.userRoleName,
          value: item.userRoleSeq,
        }));

        const roleId = this.DEPT_ROLE_ID_MAP[row.deptSeq];
        const matchedRole = roleId ? roleData.find((item: any) => item.userRoleId === roleId) : null;
        this.selectedRoleSeq = matchedRole?.userRoleSeq ?? null;

        this.buildAssignFields(employeeOptions, roleOptions);

        const deptEmpData = Array.isArray(deptEmps) ? deptEmps : (deptEmps as any)?.data || [];
        const existing = deptEmpData.find((item: any) => item.roleSeq === this.selectedRoleSeq);

        if (existing) {
          this.selectedDeptEmpSeq = existing.deptEmpSeq;
        }

        this.assignFormData = {
          regionManager: existing?.empSeq,
          coveringEmployee: existing?.cvrngEmpId || null,
          role: this.selectedRoleSeq,
          fromDate: existing?.cvrngEmpFrmDt,
          toDate: existing?.cvrngEmpToDt,
        };

        this.showAssignPopup = true;
      },
      error: () => {
        this.assignEmployees = [];
        this.buildAssignFields([], []);
        this.showAssignPopup = true;
      },
    });
  }

  handleAssignSave = (form: any) => {
    const deptHead = this.assignEmployees.find((emp: any) => emp.empSeq === form.regionManager);

    const payload = {
      deptEmpSeq: this.selectedDeptEmpSeq,
      deptSeq: this.selectedDeptHeadSeq,
      empSeq: form.regionManager,
      roleSeq: this.selectedRoleSeq,
      deptEmpNm: deptHead?.empNm ?? '',
      delFlg: 0,
      cvrngEmpId: form.coveringEmployee,
      cvrngEmpFrmDt: form.fromDate,
      cvrngEmpToDt: form.toDate,
    };

    this.setup.saveDeptEmp(payload).subscribe({
      next: () => {
        this.toastr.success(this.translate.instant('SETUP.EMPLOYEE_ASSIGNED_SUCCESSFULLY'));
        this.showAssignPopup = false;
      },
      error: () => this.toastr.error(this.translate.instant('SETUP.SAVE_FAILED')),
    });
  };
}